import { createClient } from '@supabase/supabase-js';
import { log } from '../utils/logger';
import { env, getValidationErrors } from '../utils/env';

// Get validated environment configuration
const { supabase: supabaseConfig } = env;

// Log any validation errors
const validationErrors = getValidationErrors();
if (validationErrors.length > 0) {
  log.warn('Supabase configuration issues detected', 'supabase', {
    errors: validationErrors,
  });
}

// Use Supabase's default storage key format
const STORAGE_KEY = `sb-nvuajadrzyjgotptziwn-auth-token`;

export const supabase = createClient(
  supabaseConfig.url,
  supabaseConfig.publishableKey,
  {
    auth: {
      // Ensure tokens in URL are detected and processed (important for OAuth)
      detectSessionInUrl: true,
      // Use localStorage for session persistence
      persistSession: true,
      // Automatically refresh the token before it expires
      autoRefreshToken: true,
      // Use implicit flow - simpler and more reliable across browsers
      flowType: 'implicit',
      // Use localStorage as the storage mechanism
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    },
  }
);

export { STORAGE_KEY };

// Warmup function to wake up Supabase (helps with cold start on free tier)
export async function warmupSupabaseConnection(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    await supabase
      .from('allowed_emails')
      .select('id')
      .limit(1)
      .abortSignal(controller.signal);

    clearTimeout(timeoutId);
    log.info('Supabase connection warmed up', 'supabase');
    return true;
  } catch {
    log.warn('Supabase warmup failed (will retry on first check)', 'supabase');
    return false;
  }
}

// Note: Don't call getSession() here at module load time
// Let the React auth hook handle session initialization to avoid race conditions
